import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { debounce, get } from 'lodash';
import queryString from 'query-string';
import { Type } from '@lendi/lala-react';
import Alert from '@lendi-ui/alert';
import AuthLoader from '@lendi-ui/auth-loader';
import Grid from '@lendi-ui/grid';

import {
  FIELD_PATHS,
  FIELD_VALUES,
  FREQUENCY,
  TEAMVIEW_APPLICATIONS_LAYOUT_COLS,
  URL_PARAM_CUSTOMER_ID,
} from '@funnels/constants';
import { useCustomerStoreApplicant } from '@funnels/hooks';
import { HydrationProvider } from '@funnels/providers';
import {
  fetchApplicants,
  fetchAssets,
  fetchCustomers,
  fetchEmployments,
  fetchExpenses,
  fetchLiabilities,
  updateApplication,
} from '@funnels/redux-store';
import { ReduxStoreData, StoreApplicant, StoreApplication, UseParams } from '@funnels/types';
import { CenteredMaxWidthContainer, MainContentWrapper } from '@funnels/ui-components';
import {
  getActiveBreakpointNumber,
  getBrandSpecificBaseURL,
  getDashboardUrl,
  getViewType,
  normaliseExpensesByFrequency,
} from '@funnels/utils';

import Card from './Card';
import CloneModal from './CloneModal';
import {
  CARD_BREAKPOINT_SIZES,
  CUSTOMERVIEW_CARD_BREAKPOINT_SIZE,
  SUMMARY_UNIT_DESKTOP_SIZE_LARGE,
  SUMMARY_UNIT_DESKTOP_SIZE_SMALL,
  TEAMVIEW_CARD_BREAKPOINT_SIZE,
  WINDOW_RESIZE_DEBOUNCE_DELAY,
} from './constants';
import { AlertsContainer, CustomerViewHeading } from './index.style';
import NewApplicationButtonCard from './NewApplicationButton';
import SummaryCard from './SummaryCard';
import TitleFormModal from './TitleFormModal';
import VisibilityFilters from './VisibilityFilters';

function mapState(currentState: ReduxStoreData) {
  const data = {
    applicants: currentState.applicants.data || [],
    applications: currentState.applications.data || [],
    assets: currentState.assets.data || [],
    liabilities: currentState.liabilities.data || [],
    employments: currentState.employments.data || [],
    expenses: currentState.expenses.data || [],
  };
  const loading =
    currentState.applicants.loading +
    currentState.applications.loading +
    currentState.assets.loading +
    currentState.liabilities.loading +
    currentState.employments.loading +
    currentState.expenses.loading;
  return {
    data,
    loading,
  };
}

function Applications() {
  const isCustomerView = getViewType() === Type.Customer;
  const customerApplicant = useCustomerStoreApplicant();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const { [URL_PARAM_CUSTOMER_ID]: customerId } = useParams<UseParams>();
  const queryParams = queryString.parse(location.search);
  const { data, loading } = useSelector(mapState);
  const ref = useRef<HTMLDivElement | null>(null);
  const monthlyExpenses = useMemo(
    () => normaliseExpensesByFrequency(data.expenses, FREQUENCY.MONTHLY),
    [data.expenses]
  );

  const applicants: StoreApplicant[] = get(data, FIELD_PATHS.APPLICANTS(), []);
  const nonPersonApplicants = applicants.filter((applicant) =>
    [FIELD_VALUES.TRUST, FIELD_VALUES.COMPANY].includes(
      get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_ENTITY(0))
    )
  );
  const customerApplicantType = get({ applicants: [customerApplicant] }, FIELD_PATHS.APPLICANT_TYPE(0));
  const guarantorApplicants = applicants.filter(
    (applicant) => get({ applicants: [applicant] }, FIELD_PATHS.APPLICANT_TYPE(0)) === FIELD_VALUES.GUARANTOR
  );

  /////////////////
  // State hooks //
  /////////////////

  const [activeCardIndex, setActiveCardIndex] = useState(-1);
  const [activeTabApplications, setActiveTabApplications] = useState<StoreApplication[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [numCols, setNumCols] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [applicationToEditIndex, setApplicationToEditIndex] = useState(-1);
  const [clonedFromApplicationId, setClonedFromApplicationId] = useState<null | string>(null);
  const application = activeTabApplications[activeCardIndex];
  const applicationToEdit = activeTabApplications[applicationToEditIndex];
  const applicationId = get({ applications: [application] }, FIELD_PATHS.APPLICATION_CLIENT_ID(0));
  const applicationCreatedAt = get({ applications: [application] }, FIELD_PATHS.APPLICATION_CREATED_AT(0));

  ///////////////////////////
  // Effect hook constants //
  ///////////////////////////

  // Get applications data, add window resize event listener
  const recalculateNumCols = () => {
    setNumCols(
      Math.floor(
        getActiveBreakpointNumber(TEAMVIEW_APPLICATIONS_LAYOUT_COLS) / getActiveBreakpointNumber(CARD_BREAKPOINT_SIZES)
      )
    );
  };
  const handleResizeDebounced = useCallback(debounce(recalculateNumCols, WINDOW_RESIZE_DEBOUNCE_DELAY), []);

  //////////////////
  // Effect hooks //
  //////////////////

  // handle redirection for clicking continue button in CV
  useEffect(() => {
    if (isRedirecting && activeCardIndex >= 0 && loading === 0) {
      const redirectionUrl = getDashboardUrl(applicationId);
      window.location.assign(redirectionUrl);
    }
    // eslint-disable-next-line
  }, [loading]);

  useEffect(() => {
    // Fetch customer data
    if (customerId) {
      dispatch(fetchCustomers([customerId]));
    }

    // Add window event listener
    window.addEventListener('resize', handleResizeDebounced);
    recalculateNumCols();

    return () => window.removeEventListener('resize', handleResizeDebounced);
    // eslint-disable-next-line
  }, []);

  // Set the list of applications visible on the currently active tab
  useEffect(() => {
    let filtered = data.applications.filter((a) => (activeTabIndex === 0 ? a.hidden !== true : a.hidden === true));

    // only for TV filter out non current customer applications
    if (customerId) {
      filtered = filtered.filter(
        (application) => get({ applications: [application] }, FIELD_PATHS.APPLICATION_CUSTOMER_ID(0)) === customerId
      );
    }

    setActiveTabApplications(filtered);
    // eslint-disable-next-line
  }, [data.applications, activeTabIndex]);

  // Set the total number of applications that are visible and hidden
  useEffect(() => {
    // only for TV filter out non current customer applications
    const currentCustomerApplications = customerId
      ? data.applications.filter(
          (application) => get({ applications: [application] }, FIELD_PATHS.APPLICATION_CUSTOMER_ID(0)) === customerId
        )
      : data.applications;
    const count = currentCustomerApplications.filter((a) => a.hidden !== true).length;

    setVisibleCount(count);
    setHiddenCount(currentCustomerApplications.length - count);

    // Set the active card to the last application on first load
    if (count > 0 && !isCustomerView) {
      setActiveCardIndex(count - 1);
    }
    // eslint-disable-next-line
  }, [data.applications]);

  // useLayoutEffect so that there is no 'flash' of the summary card between the time when
  // the active card is set and the fetching begins
  useLayoutEffect(() => {
    if (activeCardIndex !== -1 && activeTabApplications.length > activeCardIndex) {
      // Fetch all the relevant data to the selected application
      const activeApplication = activeTabApplications[activeCardIndex];

      if (activeApplication.id) {
        dispatch(fetchApplicants(activeApplication.id, customerId, 2));
        dispatch(fetchAssets(activeApplication.id));
        dispatch(fetchLiabilities(activeApplication.id));
        dispatch(fetchEmployments(activeApplication.id, undefined));
        dispatch(fetchExpenses(activeApplication.id));
      }
    }
    // eslint-disable-next-line
  }, [activeCardIndex, activeTabApplications]);

  ///////////////
  // Constants //
  ///////////////

  const NEW_APPLICATION_URL = `/team-view/customer/${customerId}/application/new/`;

  ///////////////
  // Functions //
  ///////////////

  const handleTabChange = (index: number) => {
    setActiveCardIndex(-1);
    setActiveTabIndex(index);
  };

  const handleCardClick = (currentIndex: number) => {
    if (currentIndex === activeCardIndex) {
      setActiveCardIndex(-1);
    } else {
      setActiveCardIndex(currentIndex);
    }
  };

  const handleTeamViewContinueClick = (e: React.SyntheticEvent<Element, Event>, id?: string) => {
    e.preventDefault();
    const continueURL = generateContinueURL(customerId, id);
    window.location.assign(continueURL);
  };

  const handleCustomerViewContinueClick = (e: React.SyntheticEvent<Element, Event>, currentIndex: number) => {
    setActiveCardIndex(currentIndex);
    setIsRedirecting(true);
  };

  const handleNewApplicationClick = () => {
    isCustomerView ? window.location.assign(getBrandSpecificBaseURL()) : window.location.assign(NEW_APPLICATION_URL);
  };

  function handleShowHideClick(e: React.SyntheticEvent<Element, Event>, clientId: string, value: boolean) {
    e.preventDefault();
    e.stopPropagation();

    const application = data.applications.find((application) => application.clientId === clientId);

    if (!application) {
      return;
    }

    dispatch(updateApplication({ ...application, hidden: value }));
  }

  function handleEditTitleClick(e: React.SyntheticEvent<Element, Event>, index: number) {
    e.preventDefault();
    e.stopPropagation();

    setActiveCardIndex(index);
    setApplicationToEditIndex(index);
  }

  function handleTitleFormModalHide() {
    setApplicationToEditIndex(-1);
  }

  function handleCloneModalShow(e: React.SyntheticEvent<Element, Event>, id: string) {
    e.preventDefault();
    e.stopPropagation();

    setClonedFromApplicationId(id);
  }

  function handleCloneModalHide() {
    setClonedFromApplicationId(null);
  }

  function handleClone() {
    history.push(`/customer/${customerId}/application/${clonedFromApplicationId}/copy/`);
  }

  //////////////////////
  // Render Functions //
  //////////////////////

  const renderApplicationsGrid = () => {
    // Add all the application cards
    const toRender = activeTabApplications.map((activeApplication, i) => {
      const primaryPurpose = get(activeApplication, 'preliminary.primaryPurpose');
      const activeApplicationProps = {
        applicationClientId: activeApplication.clientId,
        type: primaryPurpose === FIELD_VALUES.NEW_PURCHASE ? 'New purchase' : 'Refinance',
        name: activeApplication.name,
        createdAt: activeApplication.createdAt,
        updatedAt: activeApplication.updatedAt,
        isLocked: activeApplication.isLocked,
      };
      const isSelected = activeCardIndex === i;
      const isDisabled = !isSelected && loading > 0;

      if (isCustomerView) {
        return (
          <Grid.Unit key={activeApplication.clientId} size={CUSTOMERVIEW_CARD_BREAKPOINT_SIZE} offset={0}>
            <Helmet>
              <title>Your applications</title>
            </Helmet>
            <Card
              {...activeApplicationProps}
              onContainerClick={() => setActiveCardIndex(i)}
              onContinueClick={(e) => handleCustomerViewContinueClick(e, i)}
              onEditTitleClick={(e) => handleEditTitleClick(e, i)}
              isSelected={isSelected}
              isTeamView={!isCustomerView}
            />
          </Grid.Unit>
        );
      }

      return (
        <Grid.Unit key={activeApplication.clientId} size={TEAMVIEW_CARD_BREAKPOINT_SIZE} offset={0}>
          <Card
            {...activeApplicationProps}
            onContainerClick={() => handleCardClick(i)}
            onContinueClick={(e) => handleTeamViewContinueClick(e, activeApplication.id)}
            onShowHideClick={(e) => handleShowHideClick(e, activeApplication.clientId, !activeApplication.hidden)}
            onEditTitleClick={(e) => handleEditTitleClick(e, i)}
            isDisabled={isDisabled}
            isSelected={isSelected}
            isVisible={!activeApplication.hidden}
            isTeamView={!isCustomerView}
          />
        </Grid.Unit>
      );
    });

    // Add the new application button on Visible tab only
    if (activeTabIndex === 0) {
      toRender.push(
        <Grid.Unit
          key={activeTabApplications.length}
          size={isCustomerView ? CUSTOMERVIEW_CARD_BREAKPOINT_SIZE : TEAMVIEW_CARD_BREAKPOINT_SIZE}
          offset={0}
        >
          <NewApplicationButtonCard handleClick={handleNewApplicationClick} />
        </Grid.Unit>
      );
    }

    // If there is an active card, splice the summary into the correct index so that it renders on the next row
    if (activeCardIndex >= 0 && activeTabApplications.length > 0 && loading === 0 && !isCustomerView) {
      // Use activeCardIndex + 1 because we want the position, not the index
      const summaryIndex = Math.ceil((activeCardIndex + 1) / numCols) * numCols;
      const primaryPurpose = get(
        { applications: [application] },
        FIELD_PATHS.APPLICATION_PRIMARY_PURPOSE(0),
        FIELD_VALUES.NEW_PURCHASE as FIELD_VALUES.REFINANCE | FIELD_VALUES.NEW_PURCHASE | FIELD_VALUES.CONSTRUCTION_LOAN
      );
      const isCurrentLoanVisible =
        primaryPurpose === FIELD_VALUES.REFINANCE || primaryPurpose === FIELD_VALUES.CONSTRUCTION_LOAN;
      const unitDesktopSizes = !isCurrentLoanVisible
        ? SUMMARY_UNIT_DESKTOP_SIZE_SMALL
        : SUMMARY_UNIT_DESKTOP_SIZE_LARGE;

      if (application) {
        const summaryCard = (
          <Grid.Unit key={`summary-for-${application.clientId}`} size={TEAMVIEW_APPLICATIONS_LAYOUT_COLS} offset={0}>
            <Grid gutter="nil">
              <Grid.Unit size={{ xs: 4, sm: 8, md: 8, lg: 8, xl: unitDesktopSizes }} offset={0}>
                <SummaryCard
                  type={primaryPurpose}
                  isVisible={!application.hidden}
                  isCurrentLoanVisible={isCurrentLoanVisible}
                  formValues={{
                    applications: [application],
                    applicants: data.applicants,
                    assets: data.assets,
                    liabilities: data.liabilities,
                    employments: data.employments,
                    expenses: monthlyExpenses,
                  }}
                  onContinueClick={(e) => handleTeamViewContinueClick(e, application.id)}
                  onHideClick={(e) => handleShowHideClick(e, application.clientId, !application.hidden)}
                  onCloneClick={(e) => handleCloneModalShow(e, application.id!)}
                />
              </Grid.Unit>
            </Grid>
          </Grid.Unit>
        );
        toRender.splice(summaryIndex, 0, summaryCard);
      }
    }

    return toRender;
  };

  // CustomerView
  if (isCustomerView) {
    if (isRedirecting) {
      return <AuthLoader data-component="auth-loader" />;
    }

    return (
      <>
        <HydrationProvider value={['applications']}>
          <MainContentWrapper>
            <CenteredMaxWidthContainer ref={ref}>
              <Grid>
                <Grid.Unit>
                  <CustomerViewHeading>Your applications</CustomerViewHeading>
                </Grid.Unit>
                {renderApplicationsGrid()}
              </Grid>
            </CenteredMaxWidthContainer>
          </MainContentWrapper>
          <TitleFormModal
            initialValues={applicationToEdit ? { applications: [applicationToEdit] } : undefined}
            isVisible={applicationToEditIndex !== -1}
            onHide={handleTitleFormModalHide}
            onAction={handleTitleFormModalHide}
            onSubmit={handleTitleFormModalHide}
            hidePrimaryButton={customerApplicantType !== FIELD_VALUES.BORROWER || loading !== 0}
          />
        </HydrationProvider>
      </>
    );
  }

  // TeamView
  return (
    <>
      <VisibilityFilters
        activeTabIndex={activeTabIndex}
        visibleCount={visibleCount}
        hiddenCount={hiddenCount}
        onChange={handleTabChange}
      />
      {!!queryParams.e && (
        <AlertsContainer>
          {queryParams.e === '1' && (
            <Alert variant="error" heading="The copy failed.">
              Please try again. If the problem persists, create a help desk ticket.
            </Alert>
          )}
          {queryParams.e === '2' && (
            <Alert variant="warn" heading="There were difficulties copying the application.">
              Please note that all data many not have been transferred successfully. You will need to manually enter the
              missing data; or hide the application and try again.
            </Alert>
          )}
        </AlertsContainer>
      )}
      <HydrationProvider value={['applications']}>
        <MainContentWrapper>
          <CenteredMaxWidthContainer ref={ref}>
            <Grid>{renderApplicationsGrid()}</Grid>
          </CenteredMaxWidthContainer>
        </MainContentWrapper>
        <TitleFormModal
          initialValues={applicationToEdit ? { applications: [applicationToEdit] } : undefined}
          isVisible={applicationToEditIndex !== -1}
          onHide={handleTitleFormModalHide}
          onAction={handleTitleFormModalHide}
          onSubmit={handleTitleFormModalHide}
        />
        <CloneModal
          id={clonedFromApplicationId}
          onHide={handleCloneModalHide}
          onAction={handleClone}
          applicationCreatedAt={applicationCreatedAt}
          hasNonPersonApplicant={nonPersonApplicants.length > 0}
          hasGuarantorApplicant={guarantorApplicants.length > 0}
        />
      </HydrationProvider>
    </>
  );
}

function generateContinueURL(customerId?: string, applicationId?: string) {
  return `/team-view/customer/${customerId}/application/${applicationId}/dashboard/`;
}

export default Applications;
